/** Medida y tile infinito de un rapport. Puro: sin canvas ni red. */

/**
 * tartan = Vectorton textile tartán (legacy: tile/crop).
 * seamless = Vectorton textile seamless (legacy: interpretacion).
 * Sin Kind, se infiere de las marcas guardadas.
 */
function parseMarksObject(raw) {
  let marks = raw;
  if (typeof marks === "string") {
    try {
      marks = JSON.parse(marks);
    } catch {
      marks = null;
    }
  }
  return marks && typeof marks === "object" && !Array.isArray(marks)
    ? marks
    : null;
}

export function rapportToolKind(row) {
  const explicit = String(row?.Kind || "")
    .trim()
    .toLowerCase();
  if (
    explicit === "tartan" ||
    explicit === "tartán" ||
    explicit === "tile" ||
    explicit === "crop"
  ) {
    return "tartan";
  }
  if (
    explicit === "seamless" ||
    explicit === "repeat" ||
    explicit === "interpretacion" ||
    explicit === "interpretaciones" ||
    explicit === "interpret" ||
    explicit === "generate"
  ) {
    return "seamless";
  }
  const marks = parseMarksObject(row?.MarksJson ?? row?.marksJson ?? null);
  if (marks) {
    const tool = String(marks.tool || marks.engine || "")
      .trim()
      .toLowerCase();
    if (tool === "tartan" || tool === "tile" || marks.mode === "crop") {
      return "tartan";
    }
    if (
      tool === "seamless" ||
      tool === "interpretacion" ||
      marks.mode === "generate" ||
      marks.mode === "textile"
    ) {
      return "seamless";
    }
    if (marks.repeat && marks.mode !== "generate") return "tartan";
  }
  return "seamless";
}

/**
 * Dentro de Interpretaciones: cloth = cerrar junta de tejido;
 * invent = inventar print desde ramo/dibujo. null = aún no eligió.
 */
export function rapportIntent(rowOrMarks) {
  const marks =
    parseMarksObject(rowOrMarks?.MarksJson ?? rowOrMarks?.marksJson) ||
    parseMarksObject(rowOrMarks);
  const raw = String(marks?.intent || "")
    .trim()
    .toLowerCase();
  if (raw === "cloth" || raw === "tejido" || raw === "seam") return "cloth";
  if (
    raw === "invent" ||
    raw === "inventar" ||
    raw === "motif" ||
    raw === "print"
  ) {
    return "invent";
  }
  return null;
}

export function normalizeRect(rect) {
  if (!rect) return null;
  const x = Number(rect.x);
  const y = Number(rect.y);
  const w = Number(rect.w);
  const h = Number(rect.h);
  if (![x, y, w, h].every((n) => Number.isFinite(n))) return null;
  return {
    x: Math.min(x, x + w),
    y: Math.min(y, y + h),
    w: Math.abs(w),
    h: Math.abs(h),
  };
}

export function clampRectToImage(rect, width, height) {
  const n = normalizeRect(rect);
  if (!n || !(width > 0) || !(height > 0)) return null;
  const x1 = Math.max(0, n.x);
  const y1 = Math.max(0, n.y);
  const x2 = Math.min(width, n.x + n.w);
  const y2 = Math.min(height, n.y + n.h);
  const w = x2 - x1;
  const h = y2 - y1;
  if (w < 8 || h < 8) return null;
  return { x: x1, y: y1, w, h };
}

export function pxPerCm(x1, y1, x2, y2, cm) {
  const pixels = Math.hypot(Number(x2) - Number(x1), Number(y2) - Number(y1));
  const lengthCm = Number(cm);
  if (!(pixels > 0) || !(lengthCm > 0)) return null;
  return pixels / lengthCm;
}

export function roundCm(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 10) / 10;
}

export function rectSizeCm(rect, pixelsPerCm) {
  const n = normalizeRect(rect);
  const ppc = Number(pixelsPerCm);
  if (!n || !(ppc > 0)) return null;
  return {
    widthCm: roundCm(n.w / ppc),
    heightCm: roundCm(n.h / ppc),
  };
}

/** La regla va en el sentido del hilo. */
export function warpFromScaleLine(x1, y1, x2, y2) {
  const dx = Math.abs(Number(x2) - Number(x1));
  const dy = Math.abs(Number(y2) - Number(y1));
  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return null;
  if (dx === 0 && dy === 0) return null;
  return dx >= dy ? "horizontal" : "vertical";
}

/**
 * Giro (radianes) para dejar la regla en horizontal o vertical.
 * Positivo = sentido del canvas (y hacia abajo).
 */
export function deskewRadiansFromRule(x1, y1, x2, y2) {
  const dx = Number(x2) - Number(x1);
  const dy = Number(y2) - Number(y1);
  if (!Number.isFinite(dx) || !Number.isFinite(dy) || (dx === 0 && dy === 0)) {
    return null;
  }
  const angle = Math.atan2(dy, dx);
  const quarter = Math.PI / 2;
  const snapped = Math.round(angle / quarter) * quarter;
  let delta = snapped - angle;
  while (delta > Math.PI / 2) delta -= Math.PI;
  while (delta < -Math.PI / 2) delta += Math.PI;
  return delta;
}

export function isRuleSkewed(rule, minDegrees = 0.35) {
  if (!rule) return false;
  const rad = deskewRadiansFromRule(rule.x1, rule.y1, rule.x2, rule.y2);
  if (rad == null) return false;
  return Math.abs(rad) * (180 / Math.PI) >= minDegrees;
}

export function rotatedCanvasSize(width, height, radians) {
  const w = Number(width);
  const h = Number(height);
  const a = Number(radians);
  if (!(w > 0) || !(h > 0) || !Number.isFinite(a)) return null;
  const c = Math.abs(Math.cos(a)) < 1e-10 ? 0 : Math.abs(Math.cos(a));
  const s = Math.abs(Math.sin(a)) < 1e-10 ? 0 : Math.abs(Math.sin(a));
  return {
    w: Math.max(1, Math.ceil(w * c + h * s)),
    h: Math.max(1, Math.ceil(w * s + h * c)),
  };
}

export function mapPointAfterRotation(x, y, srcW, srcH, radians) {
  const size = rotatedCanvasSize(srcW, srcH, radians);
  if (!size) return null;
  const dx = Number(x) - srcW / 2;
  const dy = Number(y) - srcH / 2;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return {
    x: size.w / 2 + dx * cos - dy * sin,
    y: size.h / 2 + dx * sin + dy * cos,
  };
}

export function mapRuleAfterRotation(rule, srcW, srcH, radians) {
  if (!rule) return null;
  const a = mapPointAfterRotation(rule.x1, rule.y1, srcW, srcH, radians);
  const b = mapPointAfterRotation(rule.x2, rule.y2, srcW, srcH, radians);
  if (!a || !b) return null;
  return { x1: a.x, y1: a.y, x2: b.x, y2: b.y };
}

export function mapRectAfterRotation(rect, srcW, srcH, radians) {
  const n = normalizeRect(rect);
  if (!n) return null;
  const pts = [
    mapPointAfterRotation(n.x, n.y, srcW, srcH, radians),
    mapPointAfterRotation(n.x + n.w, n.y, srcW, srcH, radians),
    mapPointAfterRotation(n.x, n.y + n.h, srcW, srcH, radians),
    mapPointAfterRotation(n.x + n.w, n.y + n.h, srcW, srcH, radians),
  ].filter(Boolean);
  if (pts.length !== 4) return null;
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

export function rapportCmFromMarks(rule, scaleCm, repeat, corners) {
  const ppc = rule
    ? pxPerCm(rule.x1, rule.y1, rule.x2, rule.y2, scaleCm)
    : null;
  const quad = quadSizePx(corners);
  const size = quad
    ? { widthCm: roundCm(quad.w / ppc), heightCm: roundCm(quad.h / ppc) }
    : rectSizeCm(repeat, ppc);
  if (quad && !(ppc > 0)) {
    return {
      pxPerCm: ppc,
      widthCm: null,
      heightCm: null,
      direccion: rule
        ? warpFromScaleLine(rule.x1, rule.y1, rule.x2, rule.y2)
        : null,
    };
  }
  return {
    pxPerCm: ppc,
    widthCm: size?.widthCm ?? null,
    heightCm: size?.heightCm ?? null,
    direccion: rule
      ? warpFromScaleLine(rule.x1, rule.y1, rule.x2, rule.y2)
      : null,
  };
}

const TILE_PX_MIN = 32;
const TILE_PX_MAX = 4000;

/** Píxeles del tile a partir de cm de ficha y la escala de la regla. */
export function tilePxFromRapportCm(widthCm, heightCm, pixelsPerCm) {
  const ppc = Number(pixelsPerCm);
  const w = Number(widthCm);
  const h = Number(heightCm);
  if (!(ppc > 0) || !(w > 0) || !(h > 0)) return null;
  const clamp = (n) =>
    Math.max(TILE_PX_MIN, Math.min(TILE_PX_MAX, Math.round(n)));
  return { w: clamp(w * ppc), h: clamp(h * ppc) };
}

export function pushRapportHistory(stack, snapshot, max = 40) {
  const list = Array.isArray(stack) ? stack : [];
  if (!snapshot) return list;
  const next = [...list, snapshot];
  return next.length > max ? next.slice(next.length - max) : next;
}

export function popRapportHistory(stack) {
  const list = Array.isArray(stack) ? [...stack] : [];
  const item = list.pop() || null;
  return { stack: list, item };
}

export function parseMarks(raw) {
  let data = raw;
  if (typeof raw === "string") {
    try {
      data = JSON.parse(raw);
    } catch {
      data = null;
    }
  }
  if (!data || typeof data !== "object") {
    return {
      rule: null,
      repeat: null,
      corners: null,
      mode: "crop",
      intent: null,
    };
  }
  const rule = data.rule;
  const ruleOk =
    rule &&
    [rule.x1, rule.y1, rule.x2, rule.y2].every((n) =>
      Number.isFinite(Number(n)),
    );
  const repeat = normalizeRect(data.repeat);
  const mode = data.mode === "generate" ? "generate" : "crop";
  return {
    rule: ruleOk
      ? {
          x1: Number(rule.x1),
          y1: Number(rule.y1),
          x2: Number(rule.x2),
          y2: Number(rule.y2),
        }
      : null,
    repeat,
    corners: parseCorners(data.corners) || cornersFromRect(repeat),
    mode,
    intent: rapportIntent(data),
  };
}

export function cornersFromRect(rect) {
  const n = normalizeRect(rect);
  if (!n) return null;
  return [
    { x: n.x, y: n.y },
    { x: n.x + n.w, y: n.y },
    { x: n.x + n.w, y: n.y + n.h },
    { x: n.x, y: n.y + n.h },
  ];
}

export function parseCorners(raw) {
  if (!Array.isArray(raw) || raw.length !== 4) return null;
  const pts = raw.map((p) => ({ x: Number(p?.x), y: Number(p?.y) }));
  if (pts.some((p) => !Number.isFinite(p.x) || !Number.isFinite(p.y))) {
    return null;
  }
  return pts;
}

export function boundingRectFromCorners(corners) {
  const pts = parseCorners(corners);
  if (!pts) return null;
  const xs = pts.map((p) => p.x);
  const ys = pts.map((p) => p.y);
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}

export function quadSizePx(corners) {
  const pts = parseCorners(corners);
  if (!pts) return null;
  const [tl, tr, br, bl] = pts;
  const top = Math.hypot(tr.x - tl.x, tr.y - tl.y);
  const bot = Math.hypot(br.x - bl.x, br.y - bl.y);
  const left = Math.hypot(bl.x - tl.x, bl.y - tl.y);
  const right = Math.hypot(br.x - tr.x, br.y - tr.y);
  if (top < 4 || bot < 4 || left < 4 || right < 4) return null;
  return { w: (top + bot) / 2, h: (left + right) / 2 };
}

export function hitQuadCorner(corners, x, y, handle) {
  const pts = parseCorners(corners);
  if (!pts || !(handle > 0)) return -1;
  for (let i = 0; i < 4; i += 1) {
    if (Math.hypot(pts[i].x - x, pts[i].y - y) <= handle) return i;
  }
  return -1;
}

export function mapCornersAfterRotation(corners, srcW, srcH, radians) {
  const pts = parseCorners(corners);
  if (!pts) return null;
  const mapped = pts.map((p) =>
    mapPointAfterRotation(p.x, p.y, srcW, srcH, radians),
  );
  if (mapped.some((p) => !p)) return null;
  return mapped;
}

function lerp(a, b, t) {
  return a + (b - a) * t;
}

export function quadPoint(corners, u, v) {
  const pts = parseCorners(corners);
  if (!pts) return null;
  const [tl, tr, br, bl] = pts;
  return {
    x: lerp(lerp(tl.x, tr.x, u), lerp(bl.x, br.x, u), v),
    y: lerp(lerp(tl.y, tr.y, u), lerp(bl.y, br.y, u), v),
  };
}

function sampleBilinear(src, w, h, x, y) {
  if (x < 0 || y < 0 || x > w - 1 || y > h - 1) {
    return [255, 255, 255, 255];
  }
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = Math.min(w - 1, x0 + 1);
  const y1 = Math.min(h - 1, y0 + 1);
  const fx = x - x0;
  const fy = y - y0;
  const i00 = (y0 * w + x0) * 4;
  const i10 = (y0 * w + x1) * 4;
  const i01 = (y1 * w + x0) * 4;
  const i11 = (y1 * w + x1) * 4;
  const out = [0, 0, 0, 0];
  for (let c = 0; c < 4; c += 1) {
    const top = lerp(src[i00 + c], src[i10 + c], fx);
    const bot = lerp(src[i01 + c], src[i11 + c], fx);
    out[c] = Math.round(lerp(top, bot, fy));
  }
  return out;
}

/**
 * Estira un cuadrilátero de la foto hasta un rectángulo (el tile a escuadra).
 */
export function warpQuadToRgba(src, srcW, srcH, corners, destW, destH) {
  const w = destW | 0;
  const h = destH | 0;
  const pts = parseCorners(corners);
  const out = new Uint8ClampedArray(Math.max(0, w * h * 4));
  if (!pts || !(w > 0) || !(h > 0) || !src) return out;
  const denomX = Math.max(1, w - 1);
  const denomY = Math.max(1, h - 1);
  for (let y = 0; y < h; y += 1) {
    const v = y / denomY;
    for (let x = 0; x < w; x += 1) {
      const srcPt = quadPoint(pts, x / denomX, v);
      const pix = sampleBilinear(src, srcW, srcH, srcPt.x, srcPt.y);
      const i = (y * w + x) * 4;
      out[i] = pix[0];
      out[i + 1] = pix[1];
      out[i + 2] = pix[2];
      out[i + 3] = pix[3];
    }
  }
  return out;
}

/**
 * Band of the seam blend. Stays strictly inside half the shorter side
 * so opposite edges never overlap.
 */
export function seamlessBand(width, height, band) {
  const maxB = Math.floor(Math.min(width, height) / 2) - 1;
  if (maxB < 1) return 0;
  const requested =
    band == null
      ? Math.min(6, Math.round(Math.min(width, height) * 0.02))
      : Number(band);
  if (!Number.isFinite(requested)) return Math.min(4, maxB);
  return Math.max(1, Math.min(Math.round(requested), maxB));
}

function blendPair(src, out, i1, i2, t) {
  for (let c = 0; c < 4; c += 1) {
    const a = src[i1 + c];
    const b = src[i2 + c];
    const mid = (a + b) / 2;
    out[i1 + c] = Math.round(a + (mid - a) * t);
    out[i2 + c] = Math.round(b + (mid - b) * t);
  }
}

/**
 * Cross-fades opposite edges so the crop tiles without a hard cut.
 * At full blend, the first column equals the last and the first row equals the last.
 */
export function makeSeamless(src, width, height, band) {
  const w = width | 0;
  const h = height | 0;
  if (!(w > 0) || !(h > 0) || !src || src.length < w * h * 4) {
    return new Uint8ClampedArray(src || []);
  }
  const b = seamlessBand(w, h, band);
  const out = new Uint8ClampedArray(src);
  if (b < 1) return out;

  const idx = (x, y) => (y * w + x) * 4;
  for (let y = 0; y < h; y += 1) {
    for (let i = 0; i < b; i += 1) {
      const t = (b - i) / b;
      blendPair(src, out, idx(i, y), idx(w - 1 - i, y), t);
    }
  }

  const mid = new Uint8ClampedArray(out);
  for (let x = 0; x < w; x += 1) {
    for (let i = 0; i < b; i += 1) {
      const t = (b - i) / b;
      blendPair(mid, out, idx(x, i), idx(x, h - 1 - i), t);
    }
  }
  return out;
}

function lumaAt(src, i) {
  return 0.2126 * src[i] + 0.7152 * src[i + 1] + 0.0722 * src[i + 2];
}

function interiorLuma(src, w, h) {
  const x0 = Math.floor(w * 0.18);
  const x1 = Math.floor(w * 0.82);
  const y0 = Math.floor(h * 0.18);
  const y1 = Math.floor(h * 0.82);
  let sum = 0;
  let n = 0;
  const step = Math.max(1, Math.floor(Math.min(w, h) / 40));
  for (let y = y0; y < y1; y += step) {
    for (let x = x0; x < x1; x += step) {
      sum += lumaAt(src, (y * w + x) * 4);
      n += 1;
    }
  }
  return n ? sum / n : 128;
}

/** Solo píxeles casi negros del marco. No corta el print ni copia bordes. */
export function stripDarkFrame(src, width, height, maxPx = 12) {
  const w = width | 0;
  const h = height | 0;
  if (!(w > 16) || !(h > 16) || !src) {
    return new Uint8ClampedArray(src || []);
  }
  const out = new Uint8ClampedArray(src);
  const limit = Math.max(2, Math.min(maxPx, Math.floor(Math.min(w, h) / 8)));
  const blackMax = 48;
  const idx = (x, y) => (y * w + x) * 4;
  const isBlack = (i) => lumaAt(src, i) < blackMax;
  const copy = (dx, dy, sx, sy) => {
    const d = idx(dx, dy);
    const s = idx(sx, sy);
    out[d] = src[s];
    out[d + 1] = src[s + 1];
    out[d + 2] = src[s + 2];
    out[d + 3] = 255;
  };
  for (let y = 0; y < h; y += 1) {
    let good = 0;
    while (good < limit && isBlack(idx(good, y))) good += 1;
    if (good > 0 && good < limit) {
      for (let t = 0; t < good; t += 1) copy(t, y, good, y);
    }
    good = 0;
    while (good < limit && isBlack(idx(w - 1 - good, y))) good += 1;
    if (good > 0 && good < limit) {
      for (let t = 0; t < good; t += 1) copy(w - 1 - t, y, w - 1 - good, y);
    }
  }
  for (let x = 0; x < w; x += 1) {
    let good = 0;
    while (good < limit && isBlack(idx(x, good))) good += 1;
    if (good > 0 && good < limit) {
      for (let t = 0; t < good; t += 1) copy(x, t, x, good);
    }
    good = 0;
    while (good < limit && isBlack(idx(x, h - 1 - good))) good += 1;
    if (good > 0 && good < limit) {
      for (let t = 0; t < good; t += 1) copy(x, h - 1 - t, x, h - 1 - good);
    }
  }
  for (let i = 3; i < out.length; i += 4) out[i] = 255;
  return out;
}

function medianOf(vals) {
  if (!vals.length) return 0;
  const a = vals.slice().sort((p, q) => p - q);
  return a[Math.floor(a.length / 2)];
}

/**
 * Quita marco negro o blanco del borde (la junta al repetir).
 * No toca el print: solo franjas casi negras o casi blancas en todo el canto.
 */
export function clearDarkRim(src, width, height, maxPx) {
  const w = width | 0;
  const h = height | 0;
  if (!(w > 24) || !(h > 24) || !src || src.length < w * h * 4) {
    return new Uint8ClampedArray(src || []);
  }
  const out = new Uint8ClampedArray(src);
  const lim = Math.max(
    4,
    Math.min(
      maxPx == null ? Math.round(Math.min(w, h) * 0.03) : Number(maxPx),
      Math.floor(Math.min(w, h) / 8),
    ),
  );
  const idx = (x, y) => (y * w + x) * 4;
  const lumaOf = (buf, x, y) => {
    const i = idx(x, y);
    return 0.2126 * buf[i] + 0.7152 * buf[i + 1] + 0.0722 * buf[i + 2];
  };
  const colMed = (x) => {
    const vals = [];
    const step = Math.max(1, Math.floor(h / 80));
    for (let y = 0; y < h; y += step) vals.push(lumaOf(src, x, y));
    return medianOf(vals);
  };
  const rowMed = (y) => {
    const vals = [];
    const step = Math.max(1, Math.floor(w / 80));
    for (let x = 0; x < w; x += step) vals.push(lumaOf(src, x, y));
    return medianOf(vals);
  };
  const isRim = (edge, ref) =>
    (edge < 55 && edge < ref - 18) || (edge > 210 && edge > ref + 18);
  const depthFrom = (at, refAt) => {
    const ref = at(refAt);
    let n = 0;
    while (n < lim && isRim(at(n), ref)) n += 1;
    return n;
  };
  const left = depthFrom((n) => colMed(n), Math.min(w - 1, lim));
  const right = depthFrom((n) => colMed(w - 1 - n), Math.max(0, w - 1 - lim));
  const top = depthFrom((n) => rowMed(n), Math.min(h - 1, lim));
  const bottom = depthFrom((n) => rowMed(h - 1 - n), Math.max(0, h - 1 - lim));
  const copy = (dx, dy, sx, sy) => {
    const d = idx(dx, dy);
    const s = idx(sx, sy);
    out[d] = out[s];
    out[d + 1] = out[s + 1];
    out[d + 2] = out[s + 2];
    out[d + 3] = 255;
  };
  if (left > 0) {
    for (let y = 0; y < h; y += 1) {
      for (let x = 0; x < left; x += 1) copy(x, y, left, y);
    }
  }
  if (right > 0) {
    for (let y = 0; y < h; y += 1) {
      for (let t = 0; t < right; t += 1) copy(w - 1 - t, y, w - 1 - right, y);
    }
  }
  if (top > 0) {
    for (let x = 0; x < w; x += 1) {
      for (let y = 0; y < top; y += 1) copy(x, y, x, top);
    }
  }
  if (bottom > 0) {
    for (let x = 0; x < w; x += 1) {
      for (let t = 0; t < bottom; t += 1) copy(x, h - 1 - t, x, h - 1 - bottom);
    }
  }
  return out;
}

/**
 * Copia fija desde el interior: mata el gris del antialias al escalar
 * (no es blanco ni negro, clearDarkRim no lo ve).
 */
export function hardInsetEdges(src, width, height, inset = 2) {
  const w = width | 0;
  const h = height | 0;
  if (!(w > 16) || !(h > 16) || !src) {
    return new Uint8ClampedArray(src || []);
  }
  const out = new Uint8ClampedArray(src);
  const n = Math.max(
    1,
    Math.min(Number(inset) || 2, Math.floor(Math.min(w, h) / 16)),
  );
  const idx = (x, y) => (y * w + x) * 4;
  const copy = (dx, dy, sx, sy) => {
    const d = idx(dx, dy);
    const s = idx(sx, sy);
    out[d] = out[s];
    out[d + 1] = out[s + 1];
    out[d + 2] = out[s + 2];
    out[d + 3] = 255;
  };
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < n; x += 1) copy(x, y, n, y);
    for (let x = 0; x < n; x += 1) copy(w - 1 - x, y, w - 1 - n, y);
  }
  for (let x = 0; x < w; x += 1) {
    for (let y = 0; y < n; y += 1) copy(x, y, x, n);
    for (let y = 0; y < n; y += 1) copy(x, h - 1 - y, x, h - 1 - n);
  }
  return out;
}

/** Última columna = primera, última fila = primera. Sin eso se ve la costura. */
export function forceWrapEdges(src, width, height) {
  const w = width | 0;
  const h = height | 0;
  if (!(w > 2) || !(h > 2) || !src) {
    return new Uint8ClampedArray(src || []);
  }
  const out = new Uint8ClampedArray(src);
  const idx = (x, y) => (y * w + x) * 4;
  for (let y = 0; y < h; y += 1) {
    const a = idx(0, y);
    const b = idx(w - 1, y);
    out[b] = out[a];
    out[b + 1] = out[a + 1];
    out[b + 2] = out[a + 2];
    out[b + 3] = 255;
    out[a + 3] = 255;
  }
  for (let x = 0; x < w; x += 1) {
    const a = idx(x, 0);
    const b = idx(x, h - 1);
    out[b] = out[a];
    out[b + 1] = out[a + 1];
    out[b + 2] = out[a + 2];
    out[b + 3] = 255;
    out[a + 3] = 255;
  }
  return out;
}

/**
 * Deja el tile listo para repetir.
 * Tras INTERPRETAR el Fill ya está en el borde: no recortar ni copiar
 * interior encima (eso mata la invención y deja junta dura).
 */
export function sealTileEdges(src, width, height) {
  const w = width | 0;
  const h = height | 0;
  if (!(w > 24) || !(h > 24) || !src) {
    return new Uint8ClampedArray(src || []);
  }
  const rim = clearDarkRim(src, w, h);
  return forceWrapEdges(rim, w, h);
}

function colorDist2(src, i1, i2) {
  let sum = 0;
  for (let c = 0; c < 3; c += 1) {
    const d = src[i1 + c] - src[i2 + c];
    sum += d * d;
  }
  return sum;
}

function minCutPath(err, stride, rows) {
  const cost = new Float64Array(rows * stride);
  const prev = new Int16Array(rows * stride);
  for (let x = 0; x < stride; x += 1) cost[x] = err[x];
  for (let y = 1; y < rows; y += 1) {
    for (let x = 0; x < stride; x += 1) {
      let bestX = x;
      let bestC = cost[(y - 1) * stride + x];
      if (x > 0 && cost[(y - 1) * stride + x - 1] < bestC) {
        bestC = cost[(y - 1) * stride + x - 1];
        bestX = x - 1;
      }
      if (x + 1 < stride && cost[(y - 1) * stride + x + 1] < bestC) {
        bestC = cost[(y - 1) * stride + x + 1];
        bestX = x + 1;
      }
      cost[y * stride + x] = err[y * stride + x] + bestC;
      prev[y * stride + x] = bestX;
    }
  }
  const last = (rows - 1) * stride;
  let xBest = 0;
  let cBest = cost[last];
  for (let x = 1; x < stride; x += 1) {
    if (cost[last + x] < cBest) {
      cBest = cost[last + x];
      xBest = x;
    }
  }
  const path = new Int16Array(rows);
  path[rows - 1] = xBest;
  for (let y = rows - 1; y > 0; y -= 1) {
    xBest = prev[y * stride + xBest];
    path[y - 1] = xBest;
  }
  return path;
}

function copyPx(src, from, dest, to) {
  dest[to] = src[from];
  dest[to + 1] = src[from + 1];
  dest[to + 2] = src[from + 2];
  dest[to + 3] = src[from + 3];
}

/**
 * Wrap without the muddy 8% band: min-cut through the overlap, then split
 * that overlap across both tile edges so abutting reconstructs it.
 */
export function makeSeamlessMinCut(src, width, height, overlap) {
  const w = width | 0;
  const h = height | 0;
  if (!(w > 8) || !(h > 8) || !src || src.length < w * h * 4) {
    return new Uint8ClampedArray(src || []);
  }
  const maxB = Math.floor(Math.min(w, h) / 4);
  const requested =
    overlap == null ? Math.round(Math.min(w, h) * 0.06) : Number(overlap);
  const b = Math.max(8, Math.min(maxB, Math.round(requested) || 8));
  const out = new Uint8ClampedArray(src);
  stitchWrapX(out, w, h, b);
  stitchWrapY(out, w, h, b);
  return out;
}

function stitchWrapX(out, w, h, b) {
  const err = new Float64Array(h * b);
  const idx = (x, y) => (y * w + x) * 4;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < b; x += 1) {
      err[y * b + x] = colorDist2(out, idx(w - b + x, y), idx(x, y));
    }
  }
  const path = minCutPath(err, b, h);
  const half = Math.floor(b / 2);
  const row = new Uint8ClampedArray(b * 4);
  for (let y = 0; y < h; y += 1) {
    const cut = path[y];
    for (let x = 0; x < b; x += 1) {
      const from = x <= cut ? idx(w - b + x, y) : idx(x, y);
      copyPx(out, from, row, x * 4);
    }
    for (let x = 0; x < half; x += 1) {
      copyPx(row, x * 4, out, idx(w - half + x, y));
    }
    for (let x = half; x < b; x += 1) {
      copyPx(row, x * 4, out, idx(x - half, y));
    }
  }
}

/**
 * Photoshop Offset 50 %: el borde del tile es el centro de la foto
 * (al repetir encaja). El canto de la foto queda en el centro.
 */
export function offsetHalfRgba(src, width, height) {
  const w = width | 0;
  const h = height | 0;
  const out = new Uint8ClampedArray(w * h * 4);
  if (!(w > 1) || !(h > 1) || !src) return out;
  const ox = Math.floor(w / 2);
  const oy = Math.floor(h / 2);
  for (let y = 0; y < h; y += 1) {
    const sy = (y + oy) % h;
    for (let x = 0; x < w; x += 1) {
      const sx = (x + ox) % w;
      const from = (sy * w + sx) * 4;
      const to = (y * w + x) * 4;
      out[to] = src[from];
      out[to + 1] = src[from + 1];
      out[to + 2] = src[from + 2];
      out[to + 3] = src[from + 3];
    }
  }
  return out;
}

function measureWhiteHalf(buf, w, h, cx, cy) {
  const whiteAt = (x, y) => {
    const i = (y * w + x) * 4;
    return buf[i] > 236 && buf[i + 1] > 236 && buf[i + 2] > 236;
  };
  const run = (dx, dy) => {
    let n = 0;
    let x = cx;
    let y = cy;
    const cap = Math.max(w, h);
    while (n < cap && x >= 0 && y >= 0 && x < w && y < h && whiteAt(x, y)) {
      n += 1;
      x += dx;
      y += dy;
    }
    return n;
  };
  return Math.max(run(-1, 0), run(1, 0), run(0, -1), run(0, 1));
}

function dist2px(a, ia, b, ib) {
  const dr = a[ia] - b[ib];
  const dg = a[ia + 1] - b[ib + 1];
  const db = a[ia + 2] - b[ib + 2];
  return dr * dr + dg * dg + db * db;
}

function ssdBox(a, ax, ay, b, bx, by, rw, rh, w, h, limit) {
  if (
    rw <= 0 ||
    rh <= 0 ||
    ax < 0 ||
    ay < 0 ||
    bx < 0 ||
    by < 0 ||
    ax + rw > w ||
    bx + rw > w ||
    ay + rh > h ||
    by + rh > h
  ) {
    return 1e15;
  }
  let err = 0;
  let n = 0;
  const step = rw > 28 || rh > 28 ? 2 : 1;
  for (let y = 0; y < rh; y += step) {
    const aRow = (ay + y) * w;
    const bRow = (by + y) * w;
    for (let x = 0; x < rw; x += step) {
      const ia = (aRow + ax + x) * 4;
      const ib = (bRow + bx + x) * 4;
      const dr = a[ia] - b[ib];
      const dg = a[ia + 1] - b[ib + 1];
      const db = a[ia + 2] - b[ib + 2];
      err += dr * dr + dg * dg + db * db;
      n += 1;
      if (limit && n >= 8 && err > limit * n) return 1e15;
    }
  }
  return n ? err / n : 1e15;
}

function copyPxFrom(src, si, dst, di) {
  dst[di] = src[si];
  dst[di + 1] = src[si + 1];
  dst[di + 2] = src[si + 2];
  dst[di + 3] = 255;
}

function pasteVerticalCut(dst, dx, dy, src, sx, sy, rw, rh, overlap, w) {
  if (rw <= 0 || rh <= 0) return;
  const blit = (x0) => {
    for (let y = 0; y < rh; y += 1) {
      for (let x = x0; x < rw; x += 1) {
        copyPxFrom(
          src,
          ((sy + y) * w + sx + x) * 4,
          dst,
          ((dy + y) * w + dx + x) * 4,
        );
      }
    }
  };
  if (overlap < 2) {
    blit(0);
    return;
  }
  const ov = Math.min(overlap, rw - 1);
  const err = new Float64Array(rh * ov);
  for (let y = 0; y < rh; y += 1) {
    for (let x = 0; x < ov; x += 1) {
      err[y * ov + x] = dist2px(
        dst,
        ((dy + y) * w + dx + x) * 4,
        src,
        ((sy + y) * w + sx + x) * 4,
      );
    }
  }
  const path = minCutPath(err, ov, rh);
  for (let y = 0; y < rh; y += 1) {
    const cut = path[y];
    for (let x = cut + 1; x < rw; x += 1) {
      copyPxFrom(
        src,
        ((sy + y) * w + sx + x) * 4,
        dst,
        ((dy + y) * w + dx + x) * 4,
      );
    }
  }
}

function pasteHorizontalCut(dst, dx, dy, src, sx, sy, rw, rh, overlap, w) {
  if (rw <= 0 || rh <= 0) return;
  if (overlap < 2) {
    for (let y = 0; y < rh; y += 1) {
      for (let x = 0; x < rw; x += 1) {
        copyPxFrom(
          src,
          ((sy + y) * w + sx + x) * 4,
          dst,
          ((dy + y) * w + dx + x) * 4,
        );
      }
    }
    return;
  }
  const ov = Math.min(overlap, rh - 1);
  const err = new Float64Array(rw * ov);
  for (let x = 0; x < rw; x += 1) {
    for (let y = 0; y < ov; y += 1) {
      err[x * ov + y] = dist2px(
        dst,
        ((dy + y) * w + dx + x) * 4,
        src,
        ((sy + y) * w + sx + x) * 4,
      );
    }
  }
  const path = minCutPath(err, ov, rw);
  for (let x = 0; x < rw; x += 1) {
    for (let y = path[x] + 1; y < rh; y += 1) {
      copyPxFrom(
        src,
        ((sy + y) * w + sx + x) * 4,
        dst,
        ((dy + y) * w + dx + x) * 4,
      );
    }
  }
}

function wrapCutRows(buf, w, y0, y1, overlap) {
  const rows = y1 - y0;
  const ov = Math.max(2, overlap | 0);
  if (rows < 2 || ov * 2 >= w) return;
  const err = new Float64Array(rows * ov);
  for (let y = 0; y < rows; y += 1) {
    for (let x = 0; x < ov; x += 1) {
      err[y * ov + x] = dist2px(
        buf,
        ((y0 + y) * w + (w - ov + x)) * 4,
        buf,
        ((y0 + y) * w + x) * 4,
      );
    }
  }
  const path = minCutPath(err, ov, rows);
  const half = Math.floor(ov / 2);
  const row = new Uint8ClampedArray(ov * 4);
  for (let y = 0; y < rows; y += 1) {
    const cut = path[y];
    const yy = y0 + y;
    for (let x = 0; x < ov; x += 1) {
      const from = x <= cut ? (yy * w + (w - ov + x)) * 4 : (yy * w + x) * 4;
      copyPxFrom(buf, from, row, x * 4);
    }
    for (let x = 0; x < half; x += 1) {
      copyPxFrom(row, x * 4, buf, (yy * w + (w - half + x)) * 4);
    }
    for (let x = half; x < ov; x += 1) {
      copyPxFrom(row, x * 4, buf, (yy * w + (x - half)) * 4);
    }
  }
}

function wrapCutCols(buf, w, h, x0, x1, overlap) {
  const cols = x1 - x0;
  const ov = Math.max(2, overlap | 0);
  if (cols < 2 || ov * 2 >= h) return;
  const err = new Float64Array(cols * ov);
  for (let x = 0; x < cols; x += 1) {
    for (let y = 0; y < ov; y += 1) {
      err[x * ov + y] = dist2px(
        buf,
        ((h - ov + y) * w + x0 + x) * 4,
        buf,
        (y * w + x0 + x) * 4,
      );
    }
  }
  const path = minCutPath(err, ov, cols);
  const half = Math.floor(ov / 2);
  const col = new Uint8ClampedArray(ov * 4);
  for (let x = 0; x < cols; x += 1) {
    const cut = path[x];
    const xx = x0 + x;
    for (let y = 0; y < ov; y += 1) {
      const from = y <= cut ? ((h - ov + y) * w + xx) * 4 : (y * w + xx) * 4;
      copyPxFrom(buf, from, col, y * 4);
    }
    for (let y = 0; y < half; y += 1) {
      copyPxFrom(col, y * 4, buf, ((h - half + y) * w + xx) * 4);
    }
    for (let y = half; y < ov; y += 1) {
      copyPxFrom(col, y * 4, buf, ((y - half) * w + xx) * 4);
    }
  }
}

export function sampleGroundColor(buf, w, h) {
  const pts = [
    [2, 2],
    [w - 3, 2],
    [2, h - 3],
    [w - 3, h - 3],
    [(w / 2) | 0, 2],
    [(w / 2) | 0, h - 3],
    [2, (h / 2) | 0],
    [w - 3, (h / 2) | 0],
  ];
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  for (const [x, y] of pts) {
    if (x < 0 || y < 0 || x >= w || y >= h) continue;
    const i = (y * w + x) * 4;
    const hi = Math.max(buf[i], buf[i + 1], buf[i + 2]);
    const lo = Math.min(buf[i], buf[i + 1], buf[i + 2]);
    if (hi < 200 || hi - lo > 40) continue;
    r += buf[i];
    g += buf[i + 1];
    b += buf[i + 2];
    n += 1;
  }
  if (n < 3) return null;
  return [(r / n) | 0, (g / n) | 0, (b / n) | 0];
}

function isGroundPx(buf, i, ground, tol) {
  const dr = Math.abs(buf[i] - ground[0]);
  const dg = Math.abs(buf[i + 1] - ground[1]);
  const db = Math.abs(buf[i + 2] - ground[2]);
  return dr <= tol && dg <= tol && db <= tol;
}

export function contentBounds(buf, w, h, ground, tol = 28) {
  let x0 = w;
  let y0 = h;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      if (isGroundPx(buf, (y * w + x) * 4, ground, tol)) continue;
      if (x < x0) x0 = x;
      if (y < y0) y0 = y;
      if (x > x1) x1 = x;
      if (y > y1) y1 = y;
    }
  }
  if (x1 < x0 || y1 < y0) return null;
  return { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1 };
}

/** Blanco/crema de ramo — no el beige del leopard (más oscuro / más croma). */
function isLightFieldPx(buf, i, thr = 232) {
  const hi = Math.max(buf[i], buf[i + 1], buf[i + 2]);
  const lo = Math.min(buf[i], buf[i + 1], buf[i + 2]);
  const avg = (buf[i] + buf[i + 1] + buf[i + 2]) / 3;
  return avg >= thr && hi - lo < 40;
}

/** Distinto del crema de esquina: pétalo melocotón cuenta, no tarjeta blanca. */
function isGroundNearPx(buf, i, ground, thr = 32) {
  const dr = buf[i] - ground[0];
  const dg = buf[i + 1] - ground[1];
  const db = buf[i + 2] - ground[2];
  return dr * dr + dg * dg + db * db <= thr * thr;
}

/** Esquinas claras → ramo / motivo sobre fondo, no textura continua. */
export function photoHasLightGround(src, width, height) {
  const w = width | 0;
  const h = height | 0;
  if (!(w > 8) || !(h > 8) || !src) return false;
  const pts = [
    [2, 2],
    [w - 3, 2],
    [2, h - 3],
    [w - 3, h - 3],
  ];
  let light = 0;
  for (const [x, y] of pts) {
    if (isLightFieldPx(src, (y * w + x) * 4)) light += 1;
  }
  return light >= 3;
}

/**
 * Half-drop del ramo: silueta de flores sobre crema continuo (sin tarjeta
 * rectangular: esa se pisa y parece collage). Periodo = bbox × 1.14, nunca
 * menor que el bbox (si no, wrap → plasta).
 */
export function motifHalfDropSeamless(src, width, height) {
  const w = width | 0;
  const h = height | 0;
  if (!(w > 8) || !(h > 8) || !src || src.length < w * h * 4) {
    return { rgba: new Uint8ClampedArray(src || []), width: w, height: h };
  }
  let gr = 0;
  let gg = 0;
  let gb = 0;
  let gn = 0;
  const corners = [
    [2, 2],
    [w - 3, 2],
    [2, h - 3],
    [w - 3, h - 3],
  ];
  for (const [x, y] of corners) {
    const i = (y * w + x) * 4;
    if (!isLightFieldPx(src, i)) continue;
    gr += src[i];
    gg += src[i + 1];
    gb += src[i + 2];
    gn += 1;
  }
  const ground = gn
    ? [(gr / gn) | 0, (gg / gn) | 0, (gb / gn) | 0]
    : sampleGroundColor(src, w, h) || [248, 248, 248];
  // Máscara: no-fondo + 1 px de dilate para no comerse el antialias.
  const solid = new Uint8Array(w * h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      const i = (y * w + x) * 4;
      if (!isGroundNearPx(src, i, ground, 22)) solid[y * w + x] = 1;
    }
  }
  const mask = new Uint8Array(w * h);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      let keep = solid[y * w + x];
      if (!keep) {
        for (let dy = -1; dy <= 1 && !keep; dy += 1) {
          for (let dx = -1; dx <= 1; dx += 1) {
            const nx = x + dx;
            const ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= w || ny >= h) continue;
            if (solid[ny * w + nx]) {
              keep = 1;
              break;
            }
          }
        }
      }
      mask[y * w + x] = keep;
    }
  }
  let x0 = w;
  let y0 = h;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < w; x += 1) {
      if (!mask[y * w + x]) continue;
      if (x < x0) x0 = x;
      if (y < y0) y0 = y;
      if (x > x1) x1 = x;
      if (y > y1) y1 = y;
    }
  }
  if (x1 < x0 || y1 < y0) {
    return { rgba: forceWrapEdges(src, w, h), width: w, height: h };
  }
  const mw = x1 - x0 + 1;
  const mh = y1 - y0 + 1;
  const tw = Math.max(mw, Math.round(mw * 1.14));
  const th = Math.max(mh, Math.round(mh * 1.14));
  const out = new Uint8ClampedArray(tw * th * 4);
  for (let i = 0; i < out.length; i += 4) {
    out[i] = ground[0];
    out[i + 1] = ground[1];
    out[i + 2] = ground[2];
    out[i + 3] = 255;
  }
  const stamp = (dx, dy) => {
    for (let y = 0; y < mh; y += 1) {
      for (let x = 0; x < mw; x += 1) {
        const sx = x0 + x;
        const sy = y0 + y;
        if (!mask[sy * w + sx]) continue;
        const from = (sy * w + sx) * 4;
        const tx = (((dx + x) % tw) + tw) % tw;
        const ty = (((dy + y) % th) + th) % th;
        const to = (ty * tw + tx) * 4;
        out[to] = src[from];
        out[to + 1] = src[from + 1];
        out[to + 2] = src[from + 2];
        out[to + 3] = 255;
      }
    }
  };
  stamp(0, 0);
  stamp(Math.floor(tw / 2), Math.floor(th / 2));
  return {
    rgba: forceWrapEdges(out, tw, th),
    width: tw,
    height: th,
  };
}

/**
 * Motivo sobre fondo claro: el ramo entero, con aire del mismo fondo
 * alrededor. Al repetir, fondo con fondo: no corta pétalos.
 */
export function motifPadSeamless(src, width, height) {
  const w = width | 0;
  const h = height | 0;
  if (!(w > 8) || !(h > 8) || !src || src.length < w * h * 4) {
    return { rgba: new Uint8ClampedArray(src || []), width: w, height: h };
  }
  const ground = sampleGroundColor(src, w, h) || [248, 248, 248];
  const tol = 28;
  const box = contentBounds(src, w, h, ground, tol);
  if (!box || box.w < 8 || box.h < 8) {
    return { rgba: forceWrapEdges(src, w, h), width: w, height: h };
  }
  const pad = Math.max(
    12,
    Math.round(Math.max(box.w, box.h) * 0.08),
    Math.round(Math.min(w, h) * 0.04),
  );
  const outW = box.w + pad * 2;
  const outH = box.h + pad * 2;
  const out = new Uint8ClampedArray(outW * outH * 4);
  for (let i = 0; i < out.length; i += 4) {
    out[i] = ground[0];
    out[i + 1] = ground[1];
    out[i + 2] = ground[2];
    out[i + 3] = 255;
  }
  for (let y = 0; y < box.h; y += 1) {
    for (let x = 0; x < box.w; x += 1) {
      const from = ((box.y + y) * w + (box.x + x)) * 4;
      const to = ((pad + y) * outW + (pad + x)) * 4;
      out[to] = src[from];
      out[to + 1] = src[from + 1];
      out[to + 2] = src[from + 2];
      out[to + 3] = 255;
    }
  }
  return {
    rgba: forceWrapEdges(out, outW, outH),
    width: outW,
    height: outH,
  };
}

function hasLightGround(buf, w, h) {
  const ground = sampleGroundColor(buf, w, h);
  if (!ground) return false;
  const corners = [
    [2, 2],
    [w - 3, 2],
    [2, h - 3],
    [w - 3, h - 3],
  ];
  const cornersOk = corners.every(([x, y]) =>
    isGroundPx(buf, (y * w + x) * 4, ground, 28),
  );
  let groundN = 0;
  let n = 0;
  const step = Math.max(1, Math.floor(Math.min(w, h) / 80));
  for (let y = 0; y < h; y += step) {
    for (let x = 0; x < w; x += step) {
      n += 1;
      if (isGroundPx(buf, (y * w + x) * 4, ground, 28)) groundN += 1;
    }
  }
  return cornersOk || (n > 0 && groundN / n >= 0.22);
}

function seamHalfPx(minSide, whiteHalf) {
  const maxHalf = Math.max(8, Math.floor(minSide / 6) - 8);
  let half = Math.round(minSide * 0.08);
  if (whiteHalf > 2) half = Math.max(half, whiteHalf + 4);
  return Math.max(8, Math.min(half, maxHalf, 56));
}

function placeVerticalBlock(
  buf,
  corpus,
  x0,
  y,
  sx,
  sy,
  bandW,
  bh,
  ctx,
  overlap,
  w,
) {
  const ov = overlap >= 2 ? Math.min(overlap, bh - 1) : 0;
  const fullW = bandW + ctx * 2;
  const hcut = new Int16Array(fullW);
  hcut.fill(-1);
  if (ov >= 2) {
    const err = new Float64Array(fullW * ov);
    for (let x = 0; x < fullW; x += 1) {
      for (let yy = 0; yy < ov; yy += 1) {
        err[x * ov + yy] = dist2px(
          buf,
          ((y + yy) * w + (x0 - ctx + x)) * 4,
          corpus,
          ((sy + yy) * w + (sx - ctx + x)) * 4,
        );
      }
    }
    const path = minCutPath(err, ov, fullW);
    for (let x = 0; x < fullW; x += 1) hcut[x] = path[x];
  }
  for (let x = 0; x < bandW; x += 1) {
    const cut = hcut[x + ctx];
    for (let yy = cut + 1; yy < bh; yy += 1) {
      copyPxFrom(
        corpus,
        ((sy + yy) * w + sx + x) * 4,
        buf,
        ((y + yy) * w + x0 + x) * 4,
      );
    }
  }
  const cutCol = (xKeep, sxKeep, towardBand) => {
    const err = new Float64Array(bh * ctx);
    for (let yy = 0; yy < bh; yy += 1) {
      for (let x = 0; x < ctx; x += 1) {
        const bias = (towardBand ? ctx - 1 - x : x) * 40;
        err[yy * ctx + x] =
          dist2px(
            buf,
            ((y + yy) * w + xKeep + x) * 4,
            corpus,
            ((sy + yy) * w + sxKeep + x) * 4,
          ) + bias;
      }
    }
    const path = minCutPath(err, ctx, bh);
    for (let yy = 0; yy < bh; yy += 1) {
      const from = towardBand ? path[yy] + 1 : 0;
      const to = towardBand ? ctx : path[yy];
      for (let x = from; x < to; x += 1) {
        const local = towardBand ? x : x + fullW - ctx;
        if (yy <= hcut[local]) continue;
        copyPxFrom(
          corpus,
          ((sy + yy) * w + sxKeep + x) * 4,
          buf,
          ((y + yy) * w + xKeep + x) * 4,
        );
      }
    }
  };
  cutCol(x0 - ctx, sx - ctx, true);
  cutCol(x0 + bandW, sx + bandW, false);
}

function placeHorizontalBlock(
  buf,
  corpus,
  x,
  y0,
  sx,
  sy,
  bw,
  bandH,
  ctx,
  overlap,
  w,
) {
  const ov = overlap >= 2 ? Math.min(overlap, bw - 1) : 0;
  const fullH = bandH + ctx * 2;
  const vcut = new Int16Array(fullH);
  vcut.fill(-1);
  if (ov >= 2) {
    const err = new Float64Array(fullH * ov);
    for (let y = 0; y < fullH; y += 1) {
      for (let xx = 0; xx < ov; xx += 1) {
        err[y * ov + xx] = dist2px(
          buf,
          ((y0 - ctx + y) * w + x + xx) * 4,
          corpus,
          ((sy - ctx + y) * w + sx + xx) * 4,
        );
      }
    }
    const path = minCutPath(err, ov, fullH);
    for (let y = 0; y < fullH; y += 1) vcut[y] = path[y];
  }
  for (let y = 0; y < bandH; y += 1) {
    const cut = vcut[y + ctx];
    for (let xx = cut + 1; xx < bw; xx += 1) {
      copyPxFrom(
        corpus,
        ((sy + y) * w + sx + xx) * 4,
        buf,
        ((y0 + y) * w + x + xx) * 4,
      );
    }
  }
  const cutStrip = (yKeep, syKeep, towardBand) => {
    const err = new Float64Array(bw * ctx);
    for (let xx = 0; xx < bw; xx += 1) {
      for (let y = 0; y < ctx; y += 1) {
        const bias = (towardBand ? ctx - 1 - y : y) * 40;
        err[xx * ctx + y] =
          dist2px(
            buf,
            ((yKeep + y) * w + x + xx) * 4,
            corpus,
            ((syKeep + y) * w + sx + xx) * 4,
          ) + bias;
      }
    }
    const path = minCutPath(err, ctx, bw);
    for (let xx = 0; xx < bw; xx += 1) {
      const from = towardBand ? path[xx] + 1 : 0;
      const to = towardBand ? ctx : path[xx];
      for (let y = from; y < to; y += 1) {
        const local = towardBand ? y : y + fullH - ctx;
        if (xx <= vcut[local]) continue;
        copyPxFrom(
          corpus,
          ((syKeep + y) * w + sx + xx) * 4,
          buf,
          ((yKeep + y) * w + x + xx) * 4,
        );
      }
    }
  };
  cutStrip(y0 - ctx, sy - ctx, true);
  cutStrip(y0 + bandH, sy + bandH, false);
}

function quiltHorizontalBand(buf, corpus, w, h, y0, y1, x0, x1) {
  const bandH = y1 - y0;
  const ctx = Math.max(6, Math.min(28, Math.floor(bandH * 0.45)));
  const panelW = Math.min(x0, w - x1);
  const blockW = Math.max(
    16,
    Math.min(64, panelW - ctx, Math.floor(w / 6) || 16),
  );
  if (blockW < 16 || y0 < ctx || h - y1 < ctx) return;
  const overlap = Math.max(4, Math.floor(blockW * 0.3));
  const step = Math.max(1, blockW - overlap);
  const stride = w > 420 ? 4 : 2;
  const sources = [];
  for (let sy = ctx; sy + bandH + ctx <= h; sy += stride) {
    const top = sy - ctx;
    const bot = sy + bandH + ctx;
    if (top < y1 && bot > y0) continue;
    for (let sx = 0; sx + blockW <= w; sx += stride) {
      if (sx < x1 && sx + blockW > x0) continue;
      sources.push(sy * w + sx);
    }
  }
  if (!sources.length) return;
  const xs = [];
  for (let x = 0; x < w; x += step) xs.push(x);
  const tail = Math.max(0, w - blockW);
  if (xs[xs.length - 1] !== tail) xs.push(tail);
  xs.forEach((x, index) => {
    const bw = Math.min(blockW, w - x);
    let best = 1e15;
    let bsx = -1;
    let bsy = 0;
    for (let s = 0; s < sources.length; s += 1) {
      const sx = sources[s] % w;
      const sy = (sources[s] / w) | 0;
      if (sx + bw > w) continue;
      const above = ssdBox(
        buf,
        x,
        y0 - ctx,
        corpus,
        sx,
        sy - ctx,
        bw,
        ctx,
        w,
        h,
        best,
      );
      if (above >= 1e14) continue;
      const below = ssdBox(
        buf,
        x,
        y1,
        corpus,
        sx,
        sy + bandH,
        bw,
        ctx,
        w,
        h,
        best,
      );
      let err = above + below;
      if (index > 0) {
        const ov = Math.min(overlap, bw);
        err +=
          ssdBox(
            buf,
            x,
            y0 - ctx,
            corpus,
            sx,
            sy - ctx,
            ov,
            bandH + ctx * 2,
            w,
            h,
            best,
          ) * 2;
      }
      if (err < best) {
        best = err;
        bsx = sx;
        bsy = sy;
      }
    }
    if (bsx < 0) return;
    placeHorizontalBlock(
      buf,
      corpus,
      x,
      y0,
      bsx,
      bsy,
      bw,
      bandH,
      ctx,
      index === 0 ? 0 : overlap,
      w,
    );
  });
  wrapCutRows(buf, w, y0, y1, overlap);
}

function quiltVerticalBand(buf, corpus, w, h, x0, x1, y0, y1) {
  const bandW = x1 - x0;
  const ctx = Math.max(6, Math.min(28, Math.floor(bandW * 0.45)));
  const panelH = Math.min(y0, h - y1);
  const blockH = Math.max(
    16,
    Math.min(64, panelH - ctx, Math.floor(h / 6) || 16),
  );
  if (blockH < 16 || x0 < ctx || w - x1 < ctx) return;
  const overlap = Math.max(4, Math.floor(blockH * 0.3));
  const step = Math.max(1, blockH - overlap);
  const stride = h > 420 ? 4 : 2;
  const sources = [];
  for (let sy = 0; sy + blockH <= h; sy += stride) {
    if (sy < y1 && sy + blockH > y0) continue;
    for (let sx = ctx; sx + bandW + ctx <= w; sx += stride) {
      const left = sx - ctx;
      const right = sx + bandW + ctx;
      if (left < x1 && right > x0) continue;
      sources.push(sy * w + sx);
    }
  }
  if (!sources.length) return;
  const ys = [];
  for (let y = 0; y < h; y += step) ys.push(y);
  const tail = Math.max(0, h - blockH);
  if (ys[ys.length - 1] !== tail) ys.push(tail);
  ys.forEach((y, index) => {
    const bh = Math.min(blockH, h - y);
    let best = 1e15;
    let bsx = -1;
    let bsy = 0;
    for (let s = 0; s < sources.length; s += 1) {
      const sx = sources[s] % w;
      const sy = (sources[s] / w) | 0;
      if (sy + bh > h) continue;
      const left = ssdBox(
        buf,
        x0 - ctx,
        y,
        corpus,
        sx - ctx,
        sy,
        ctx,
        bh,
        w,
        h,
        best,
      );
      if (left >= 1e14) continue;
      const right = ssdBox(
        buf,
        x1,
        y,
        corpus,
        sx + bandW,
        sy,
        ctx,
        bh,
        w,
        h,
        best,
      );
      let err = left + right;
      if (index > 0) {
        const ov = Math.min(overlap, bh);
        err +=
          ssdBox(
            buf,
            x0 - ctx,
            y,
            corpus,
            sx - ctx,
            sy,
            bandW + ctx * 2,
            ov,
            w,
            h,
            best,
          ) * 2;
      }
      if (err < best) {
        best = err;
        bsx = sx;
        bsy = sy;
      }
    }
    if (bsx < 0) return;
    placeVerticalBlock(
      buf,
      corpus,
      x0,
      y,
      bsx,
      bsy,
      bandW,
      bh,
      ctx,
      index === 0 ? 0 : overlap,
      w,
    );
  });
  wrapCutCols(buf, w, h, x0, x1, overlap);
}

/**
 * Fondo claro: ramo con aire alrededor (fondo con fondo).
 * Textura continua: Offset + quilting sobre el corte del centro.
 */
export function contentAwareSeamless(src, width, height) {
  const w = width | 0;
  const h = height | 0;
  if (!(w > 24) || !(h > 24) || !src || src.length < w * h * 4) {
    return { rgba: new Uint8ClampedArray(src || []), width: w, height: h };
  }
  if (hasLightGround(src, w, h)) {
    return motifPadSeamless(src, w, h);
  }
  const buf = offsetHalfRgba(src, w, h);
  const corpus = new Uint8ClampedArray(buf);
  const cx = Math.floor(w / 2);
  const cy = Math.floor(h / 2);
  const half = seamHalfPx(
    Math.min(w, h),
    measureWhiteHalf(corpus, w, h, cx, cy),
  );
  const x0 = cx - half;
  const x1 = cx + half;
  const y0 = cy - half;
  const y1 = cy + half;
  if (x0 < 16 || y0 < 16 || w - x1 < 16 || h - y1 < 16) {
    return { rgba: forceWrapEdges(buf, w, h), width: w, height: h };
  }
  quiltHorizontalBand(buf, corpus, w, h, y0, y1, x0, x1);
  quiltVerticalBand(buf, corpus, w, h, x0, x1, y0, y1);
  return { rgba: forceWrapEdges(buf, w, h), width: w, height: h };
}

function lumaPx(buf, i) {
  return buf[i] * 0.2126 + buf[i + 1] * 0.7152 + buf[i + 2] * 0.0722;
}

/**
 * Tras el Offset, la junta está en el centro.
 * Photoshop / Substance: min-cut por el solape (Efros), sin fundido blanco.
 * El corte busca el hueco del dibujo (blanco entre flores, pelo entre manchas).
 */
function healCenterVertical(out, w, h, band) {
  const b = Math.max(8, Math.min(band | 0, Math.floor(w / 3)));
  const cx = Math.floor(w / 2);
  if (cx - b < 0 || cx + b > w) return;
  const idx = (x, y) => (y * w + x) * 4;
  const err = new Float64Array(h * b);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < b; x += 1) {
      const xL = cx - b + x;
      const xR = cx + x;
      const iL = idx(xL, y);
      const iR = idx(xR, y);
      const color = colorDist2(out, iL, iR);
      const gL = Math.abs(
        lumaPx(out, iL) - lumaPx(out, idx(Math.min(w - 1, xL + 1), y)),
      );
      const gR = Math.abs(
        lumaPx(out, iR) - lumaPx(out, idx(Math.min(w - 1, xR + 1), y)),
      );
      err[y * b + x] = color + (gL + gR) * 18;
    }
  }
  const path = minCutPath(err, b, h);
  const dest = cx - Math.floor(b / 2);
  const left = new Uint8ClampedArray(h * b * 4);
  const right = new Uint8ClampedArray(h * b * 4);
  for (let y = 0; y < h; y += 1) {
    for (let x = 0; x < b; x += 1) {
      copyPx(out, idx(cx - b + x, y), left, (y * b + x) * 4);
      copyPx(out, idx(cx + x, y), right, (y * b + x) * 4);
    }
  }
  for (let y = 0; y < h; y += 1) {
    const cut = path[y];
    for (let x = 0; x < b; x += 1) {
      const dx = dest + x;
      if (dx <= 0 || dx >= w - 1) continue;
      copyPx(x <= cut ? left : right, (y * b + x) * 4, out, idx(dx, y));
    }
  }
}

function healCenterHorizontal(out, w, h, band) {
  const b = Math.max(8, Math.min(band | 0, Math.floor(h / 3)));
  const cy = Math.floor(h / 2);
  if (cy - b < 0 || cy + b > h) return;
  const idx = (x, y) => (y * w + x) * 4;
  const err = new Float64Array(w * b);
  for (let x = 0; x < w; x += 1) {
    for (let y = 0; y < b; y += 1) {
      const yT = cy - b + y;
      const yB = cy + y;
      const iT = idx(x, yT);
      const iB = idx(x, yB);
      const color = colorDist2(out, iT, iB);
      const gT = Math.abs(
        lumaPx(out, iT) - lumaPx(out, idx(x, Math.min(h - 1, yT + 1))),
      );
      const gB = Math.abs(
        lumaPx(out, iB) - lumaPx(out, idx(x, Math.min(h - 1, yB + 1))),
      );
      err[x * b + y] = color + (gT + gB) * 18;
    }
  }
  const path = minCutPath(err, b, w);
  const dest = cy - Math.floor(b / 2);
  const top = new Uint8ClampedArray(b * w * 4);
  const bottom = new Uint8ClampedArray(b * w * 4);
  for (let y = 0; y < b; y += 1) {
    for (let x = 0; x < w; x += 1) {
      copyPx(out, idx(x, cy - b + y), top, (y * w + x) * 4);
      copyPx(out, idx(x, cy + y), bottom, (y * w + x) * 4);
    }
  }
  for (let x = 0; x < w; x += 1) {
    const cut = path[x];
    for (let y = 0; y < b; y += 1) {
      const dy = dest + y;
      if (dy <= 0 || dy >= h - 1) continue;
      copyPx(y <= cut ? top : bottom, (y * w + x) * 4, out, idx(x, dy));
    }
  }
}

/**
 * Como Photoshop / Substance al hacer un rapport de foto:
 * 1) Offset 50 % → las juntas entre tiles ya encajan.
 * 2) Min-cut en la cruz del centro → tapa el canto de la foto
 *    sin inventar y sin la raya blanca del fundido.
 */
export function illustratorSeamless(src, width, height) {
  const w = width | 0;
  const h = height | 0;
  if (!(w > 8) || !(h > 8) || !src || src.length < w * h * 4) {
    return { rgba: new Uint8ClampedArray(src || []), width: w, height: h };
  }
  const out = offsetHalfRgba(src, w, h);
  const band = Math.max(24, Math.min(96, Math.round(Math.min(w, h) * 0.12)));
  healCenterVertical(out, w, h, band);
  healCenterHorizontal(out, w, h, band);
  return {
    rgba: forceWrapEdges(out, w, h),
    width: w,
    height: h,
  };
}

function stitchWrapY(out, w, h, b) {
  const err = new Float64Array(w * b);
  const idx = (x, y) => (y * w + x) * 4;
  for (let x = 0; x < w; x += 1) {
    for (let y = 0; y < b; y += 1) {
      err[x * b + y] = colorDist2(out, idx(x, h - b + y), idx(x, y));
    }
  }
  const path = minCutPath(err, b, w);
  const half = Math.floor(b / 2);
  const col = new Uint8ClampedArray(b * 4);
  for (let x = 0; x < w; x += 1) {
    const cut = path[x];
    for (let y = 0; y < b; y += 1) {
      const from = y <= cut ? idx(x, h - b + y) : idx(x, y);
      copyPx(out, from, col, y * 4);
    }
    for (let y = 0; y < half; y += 1) {
      copyPx(col, y * 4, out, idx(x, h - half + y));
    }
    for (let y = half; y < b; y += 1) {
      copyPx(col, y * 4, out, idx(x, y - half));
    }
  }
}

function sanitizeDriveSegment(value = "") {
  return (
    String(value)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[<>:"/\\|?*]+/g, "-")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 80) || "sin-nombre"
  );
}

export function rapportDrivePathLabel(row = {}) {
  return [
    "Agentes-STAGE",
    "temporadas",
    sanitizeDriveSegment(row.temporada || row.Temporada || "sin-temporada"),
    sanitizeDriveSegment(
      row.customerName ||
        row.CustomerName ||
        `cliente-${row.customerId || row.CustomerId || "x"}`,
    ),
    "Rapports",
    sanitizeDriveSegment(
      row.titulo || row.Titulo || `rapport-${row.id || "x"}`,
    ),
  ].join(" / ");
}
